import { createContext, useCallback, useContext, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { call, isApiError, useApiQuery } from '@hlt/web-kit/api';
import { useT } from '@hlt/web-kit/i18n';
import { FeedbackAlert, Button, ResponsiveDialog, Field, FieldInput } from '@hlt/web-kit/ui';
import { authErrorMessage } from '@/features/auth/auth-error-message';
import { SecondFactorField } from '@/features/auth/second-factor-field';

type Runner = <T>(action: () => Promise<T>) => Promise<T>;

const StepUpContext = createContext<Runner>(async (action) => await action());

/**
 * Wrap sensitive mutations: when the API answers `recent_auth_required`, ask for the password
 * (and the second factor when enrolled), issue a five-minute proof and retry the action once.
 */
export function StepUpProvider({ children }: { children: ReactNode }) {
    const t = useT();
    const [open, setOpen] = useState(false);
    // Only signed-in users can reach a sensitive action; avoid a session probe on public pages.
    const session = useApiQuery('getSession', {}, { enabled: open, retry: false });
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    // While impersonating, the step-up is the operator's password and second factor.
    const secondFactor = Boolean(session.data?.mfaEnrolled || session.data?.impersonation);
    const pending = useRef<{ resolve: () => void; reject: (e: unknown) => void } | null>(null);

    const run = useCallback<Runner>(async (action) => {
        try {
            return await action();
        } catch (error) {
            if (!isApiError(error) || error.code !== 'recent_auth_required') throw error;
            await new Promise<void>((resolve, reject) => {
                pending.current = { resolve, reject };
                setPassword('');
                setCode('');
                setError(null);
                setOpen(true);
            });
            return await action();
        }
    }, []);

    async function confirm(event: FormEvent) {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await call('reauthenticate', {
                input: { password, ...(secondFactor ? { secondFactor: code.trim() } : {}) },
            });
            setOpen(false);
            pending.current?.resolve();
            pending.current = null;
        } catch (e) {
            setError(
                authErrorMessage(e, t, (error) => {
                    if (error.fieldError('password')) return t('auth.passwordIncorrect');
                    if (error.fieldError('secondFactor')) return t('auth.mfaCodeInvalid');
                    return null;
                }),
            );
        } finally {
            setBusy(false);
        }
    }

    return (
        <StepUpContext.Provider value={run}>
            {children}
            <ResponsiveDialog
                open={open}
                onOpenChange={(next) => {
                    if (!next) {
                        pending.current?.reject(new Error('cancelled'));
                        pending.current = null;
                    }
                    setOpen(next);
                }}
                title={t('account.stepUpTitle')}
                description={t('account.stepUpLead')}
                closeLabel={t('common.close')}
                // Mounted with the app, so a dialog opened later would cover it (for example "Use the app as").
                zIndex={300}
            >
                <form ref={formRef} id="step-up-form" onSubmit={(e) => void confirm(e)} className="flex flex-col gap-4">
                    {error ? <FeedbackAlert tone="danger">{error}</FeedbackAlert> : null}
                    <Field label={t('auth.password')}>
                        <FieldInput
                            type="password"
                            autoComplete="current-password"
                            autoFocus
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </Field>
                    {secondFactor ? (
                        <SecondFactorField
                            label={t('account.stepUpCode')}
                            value={code}
                            onValueChange={setCode}
                            // Six digits submit like everywhere else, once the password is there.
                            onComplete={() => {
                                if (password) formRef.current?.requestSubmit();
                            }}
                        />
                    ) : null}
                    <div className="flex justify-end gap-2">
                        <Button variant="default" onClick={() => setOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" loading={busy} disabled={!password}>
                            {t('account.stepUpConfirm')}
                        </Button>
                    </div>
                </form>
            </ResponsiveDialog>
        </StepUpContext.Provider>
    );
}

export const useStepUp = () => useContext(StepUpContext);
